import SkeletonRow from "@/components/SkeletonRow"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import api from "@/lib/axios"
import { DataPendaftaran, PaginationData } from "@/types"
import { AxiosError } from "axios"
import { Loader2 } from "lucide-react"
import { SetStateAction, useEffect, useState } from "react"
import toast from "react-hot-toast"
import { HiOutlineChevronLeft, HiOutlineChevronRight } from "react-icons/hi2"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useLocation, useNavigate } from "react-router-dom"

type InputState = {
  nomor_pendaftaran: string
  is_lulus: boolean
}

const patchDataKelulusan = async (data: InputState[]) => {
  const response = await api.patch("/data-pendaftaran/set-kelulusan-rpl", {
    data,
  })
  return response.data
}

export default function FormUpdateRpl({
  isPendingListDataPendaftaran,
  listDataPendaftaran,
  closeModal,
  searchQuery,
  setSearchQuery,
}: {
  isPendingListDataPendaftaran: boolean
  listDataPendaftaran: PaginationData<DataPendaftaran[]>
  closeModal: () => void
  searchQuery: string
  setSearchQuery: React.Dispatch<SetStateAction<string>>
}) {
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const [form, setForm] = useState<InputState[]>([])

  const queryClient = useQueryClient()
  const { mutate, isPending } = useMutation({
    mutationFn: patchDataKelulusan,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["listDataPendaftaran"] })
      queryClient.invalidateQueries({
        queryKey: ["listDataPendaftaranRpl"],
      })
      toast.success(data?.message || "Success.")
    },
    onError: (error: AxiosError) => {
      const err = error as AxiosError<{ message?: string }>
      toast.error(
        err?.response?.data?.message || "An unexpected error occurred."
      )
    },
  })

  const handleUpdateKelulusan = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    mutate(form)
  }

  useEffect(() => {
    if (!listDataPendaftaran?.data) return

    setForm(
      listDataPendaftaran.data.map((item) => ({
        nomor_pendaftaran: item.nomor_pendaftaran ?? "",
        is_lulus: Boolean(item.is_lulus) ?? false,
      }))
    )
  }, [listDataPendaftaran])

  return (
    <form onSubmit={handleUpdateKelulusan}>
      <div className="mb-3">
        <Input
          placeholder="Search..."
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.target.value)}
        />
      </div>
      <table className="table-auto w-full border-collapse border rounded-xl overflow-hidden">
        <thead>
          <tr>
            <th className="px-2 py-1 font-semibold border" rowSpan={2}>
              Nomor Pendaftaran
            </th>
            <th className="px-2 py-1 font-semibold border" rowSpan={2}>
              Nama Lengkap
            </th>
            <th className="px-2 py-1 font-semibold border" colSpan={2}>
              Status Kelulusan
            </th>
          </tr>
          <tr>
            <th className="px-2 py-1 font-semibold border">Tidak Lulus</th>
            <th className="px-2 py-1 font-semibold border">Lulus</th>
          </tr>
        </thead>
        <tbody>
          {isPendingListDataPendaftaran
            ? Array(5)
                .fill(null)
                .map((_, index) => {
                  return (
                    <tr key={index} className="mt-3">
                      <td className="px-2 py-1 border">
                        <SkeletonRow />
                      </td>
                      <td className="px-2 py-1 border">
                        <SkeletonRow />
                      </td>
                      <td className="px-2 py-1 border">
                        <SkeletonRow />
                      </td>
                      <td className="px-2 py-1 border">
                        <SkeletonRow />
                      </td>
                    </tr>
                  )
                })
            : listDataPendaftaran?.data?.map((item) => {
                const dataPendaftaran = form.find(
                  (itemForm) =>
                    itemForm.nomor_pendaftaran === item.nomor_pendaftaran
                )

                const handleLulusChange = (isLulus: boolean) => {
                  const nomorPendaftaran = item.nomor_pendaftaran || ""

                  setForm((prevForm) => {
                    const existingIndex = prevForm.findIndex(
                      (entry) => entry.nomor_pendaftaran === nomorPendaftaran
                    )

                    if (existingIndex > -1) {
                      // Update data jika sudah ada
                      const updatedForm = [...prevForm]
                      updatedForm[existingIndex] = {
                        ...updatedForm[existingIndex],
                        is_lulus: isLulus,
                      }
                      return updatedForm
                    } else {
                      // Tambahkan data baru jika belum ada
                      return [
                        ...prevForm,
                        {
                          nomor_pendaftaran: nomorPendaftaran,
                          is_lulus: isLulus,
                        },
                      ]
                    }
                  })
                }
                return (
                  <tr key={item.id_data_pendaftaran}>
                    <td className="px-2 py-1 border">
                      {item.nomor_pendaftaran}
                    </td>
                    <td className="px-2 py-1 border">
                      {item?.data_siswa?.nama_lengkap}
                    </td>
                    <td className="px-2 py-1 border">
                      <div className="flex justify-center">
                        <Checkbox
                          checked={dataPendaftaran?.is_lulus === false}
                          onCheckedChange={() => handleLulusChange(false)}
                        />
                      </div>
                    </td>
                    <td className="px-2 py-1 border">
                      <div className="flex justify-center">
                        <Checkbox
                          checked={dataPendaftaran?.is_lulus === true}
                          onCheckedChange={() => handleLulusChange(true)}
                        />
                      </div>
                    </td>
                  </tr>
                )
              })}
        </tbody>
      </table>
      <div className="my-3 flex justify-between">
        <Button
          type="button"
          variant="outline"
          className="h-8 w-8 p-0"
          onClick={() =>
            listDataPendaftaran?.current_page > 1 &&
            navigate(
              `${pathname}?page=${listDataPendaftaran?.current_page - 1}${
                searchQuery ? `&search=${searchQuery}` : ""
              }`
            )
          }
          disabled={listDataPendaftaran?.current_page === 1}
        >
          <span className="sr-only">Go to previous page</span>
          <HiOutlineChevronLeft />
        </Button>
        <Button
          type="button"
          variant="outline"
          className="h-8 w-8 p-0"
          onClick={() =>
            listDataPendaftaran.current_page < listDataPendaftaran?.last_page &&
            navigate(
              `${pathname}?page=${listDataPendaftaran?.current_page + 1}${
                searchQuery ? `&search=${searchQuery}` : ""
              }`
            )
          }
          disabled={
            listDataPendaftaran?.current_page === listDataPendaftaran?.last_page
          }
        >
          <span className="sr-only">Go to next page</span>
          <HiOutlineChevronRight />
        </Button>
      </div>
      <div className="mt-7 flex gap-3">
        <Button
          type="button"
          variant={"outline"}
          className="w-full"
          disabled={isPending}
          onClick={() => closeModal()}
        >
          Batal
        </Button>
        <Button
          variant={"default"}
          className="w-full flex items-center gap-3"
          disabled={isPending}
        >
          {isPending ? (
            <>
              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              Processing...
            </>
          ) : (
            "Simpan"
          )}
        </Button>
      </div>
    </form>
  )
}
